import Button from "../Button/Button";
import { LicenseUnitSelector } from "../LicenseUnitSelector/LicenseUnitSelector";
import Input from "../Input/Input";
import { ReaderModel } from "../../constants/readers-model";
import Select from "../Select/Select";
import Modal from 'react-modal';
import { customStyles } from "../Modal/ModalComponent";
import { IoClose } from "react-icons/io5";
import { useEffect } from "react";
import { debounce } from "../../utils/timer";
import { useState } from "react";
import { useSSEContext } from "../../contexts/sse";
import { slugify } from "../../utils/string";
import { TbFaceIdError } from "react-icons/tb";
import MqttService from "../../services/MqttService";
import { v4 as uuidv4 } from "uuid";
import ReadersService from "../../services/ReadersService";
import { useCallback } from "react";
import { base64ToFile } from "../../utils/file";
import { useUserStore } from "../../contexts/userStore";
import { IoMdFingerPrint } from "react-icons/io";
import { FaTimes } from "react-icons/fa";
import { FaRegCircleCheck, FaRegCircleXmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

let timeoutInterval = null;

export const ReaderCaptureModal = ({
   onConfirm,
   handleFileLoaded,
   handleFingerLoaded,
   showReaderModal,
   setShowReaderModal,
   captureOptions,
   ...props
}) => {
   const { t } = useTranslation();
   const [readers, setReaders] = useState([]);
   const [readerModel, setReaderModel] = useState(`0`);
   const [searchReader, setSearchReader] = useState("");
   const { condominiums, selectedCondominioObj } = useUserStore();
   const [selectedReader, setSelectedReader] = useState(null);
   const [isCapturing, setIsCapturing] = useState(false);
   const [capturedData, setCapturedData] = useState(null);
   const [unit, setUnit] = useState(null);
   const { sseReceived, subscribeToActions, unsubscribeToActions, subscribedActions } = useSSEContext();
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      setUnit(selectedCondominioObj);
   }, [selectedCondominioObj]);

   function handleCancel() {
      resetCaptureState();
      setShowReaderModal(false);
      setSearchReader("");
      setReaderModel("0");
      setReaders([]);
   }

   useEffect(() => {
      if (sseReceived?.action === 'captureReply' && sseReceived.content.mqtt_id === capturedData?.mqtt_id) {
         const status = sseReceived.content.status;
         setCapturedData(prevData => ({
            ...prevData,
            uuid: sseReceived.content.uuid,
            status,
         }));
         if (status !== 'started') {
            setIsCapturing(false);
         }
      }
      if (sseReceived?.action === 'captureData' && sseReceived.content.uuid === capturedData?.uuid) {
         const status = sseReceived.content.status;
         setCapturedData(prevData => ({
            ...prevData,
            status,
            faceData: sseReceived.content.faceData,
            fingerData: sseReceived.content.fingerData,
         }));
         clearTimeout(timeoutInterval);
         setIsCapturing(false);
      }
   }, [sseReceived]);

   async function fetchReaders(search, modelArg) {
      if (!unit) return;
      try {
         setIsLoading(true);
         const model = modelArg !== '0' ? modelArg : null;
         const response = await ReadersService.getPaginatedReaders(1, search, model, unit.id, 'name');
         setIsLoading(false);
         return response.data?.items;
      } catch (error) {
         console.error('Error fetching readers:', error);
         setIsLoading(false);
         return [];
      }
   }

   const fetchReadersDebounce = useCallback(
      debounce(fetchReaders, 700), []
   );

   useEffect(() => {
      async function loadReaders() {
         const readers = await fetchReadersDebounce(searchReader, readerModel);
         setReaders(readers);
      }
      loadReaders();
   }, [searchReader]);

   useEffect(() => {
      if (!showReaderModal) return;
      async function loadReaders() {
         const readers = await fetchReaders(searchReader, readerModel);
         setReaders(readers);
      }
      loadReaders();
   }, [readerModel, showReaderModal, unit]);

   useEffect(() => {
      if (!readers || readers.length === 0) return;
      resetCaptureState();
   }, [readers]);

   function resetCaptureState() {
      clearTimeout(timeoutInterval);
      setIsCapturing(false);
      setCapturedData(null);
      setSelectedReader(null);
      if (unit) {
         unsubscribeToActions(['captureReply', 'captureData'], unit.id);
      }
   }

   function showCaptureContent() {
      if (!selectedReader) {
         return (
            <div className="no-events-wrapper aspect-square">
               <h2 className="text-[1rem]! leading-[1.2]! opacity-60!">
                  {t('captureModal.selectReaderHelpText', {
                     type: t(`common.${captureOptions.type === 'face' ? 'photo' : 'fingerprint'}`)
                  })}
               </h2>
            </div>
         );
      }
      if (isCapturing) {
         return (
            <div className="no-events-wrapper aspect-square">
               {captureOptions.type === 'face' ? (
                  <>
                     <div className="detect-loading">
                        <div className="spinner" />
                     </div>
                     <h2 className="text-[1rem]! opacity-60!">{t('captureModal.capturing')}</h2>
                  </>
               ) : (
                  <>
                     <div className="relative">
                        <IoMdFingerPrint size={140} className="relative z-10 **:fill-transparent **:stroke-(--textColor) opacity-30 **:stroke-[8px]" />
                        <IoMdFingerPrint size={140} className="absolute! fingerprint-scanner top-0 left-0 z-20" />
                        <div className="absolute z-30 fingerprint-scanner-line top-0 left-1/2 w-[calc(100%+20px)] h-1 rounded-2xl bg-(--primaryColor) shadow-(--primaryColor) -translate-x-1/2">
                        </div>
                     </div>
                     <h2 className="text-[1rem]! opacity-60! mt-5!">{t('captureModal.capturing')}</h2>
                  </>
               )}
            </div>
         );
      }
      if (capturedData) {
         return (
            <div className={`no-events-wrapper aspect-square relative ${capturedData.status !== "finished" ? 'border-red-400!' : ''}`}>
               {capturedData.status !== "finished" && (
                  <div className="size-full absolute inset-0 flex justify-center items-center flex-col gap-3 px-4! box-border!">
                     {captureOptions.type === 'face' ? (
                        <TbFaceIdError size={52} className="text-red-400!" />
                     ) : (
                        <div className="relative!">
                           <IoMdFingerPrint size={62} className="text-red-400!" />
                        </div>
                     )}
                     <h2 className="w-full text-[1rem]! text-red-400! opacity-100! text-center leading-[1.2]! flex items-center gap-2">
                        {t('captureModal.errorState', {
                           type: t(`common.${captureOptions.type === 'face' ? 'photo' : 'fingerprint'}`),
                           readerName: selectedReader?.name
                        })}
                     </h2>
                  </div>
               )}
               {capturedData.faceData && (
                  <img src={`data:image/jpeg;base64,${capturedData.faceData}`} className="absolute -inset-[2px] object-center object-cover rounded-[8px] max-w-none! w-[calc(100%+4px)] h-[calc(100%+4px)]" />
               )}
               {capturedData.fingerData && (
                  <>
                     <div className="relative">
                        <IoMdFingerPrint size={140} className="relative! text-green-400" />
                     </div>
                     <h2 className="text-[1rem]! opacity-60! mt-5! flex items-center gap-2 text-green-400!">
                        <FaRegCircleCheck size={18} />
                        {t('captureModal.successState')}
                     </h2>
                  </>
               )}
            </div>
         );
      }
   }

   async function handleCapture(reader) {
      subscribeToActions(['captureReply', 'captureData'], unit.id);
      setSelectedReader(reader);
      setIsCapturing(true);
      const data = {
         mqtt_id: uuidv4(),
      }
      await MqttService.sendCaptureRequest({
         mqtt_id: data.mqtt_id,
         finger: captureOptions.finger,
         type: captureOptions.type === 'face' ? 'FACE' : 'FINGERPRINT',
         readerUuid: reader.uuid,
      });
      setCapturedData(data);
      clearTimeout(timeoutInterval);
      timeoutInterval = setTimeout(() => {
         setIsCapturing(false);
         setCapturedData({
            status: 'timeout'
         });
      }, 70000);
   }

   async function handleConfirmCapture() {
      if (captureOptions.type === 'face') {
         const file = await base64ToFile(capturedData.faceData, `${selectedReader ? slugify(selectedReader.name) : 'reader'}-face.jpg`, 'image/jpeg');
         handleFileLoaded?.(file);
      } else {
         handleFingerLoaded?.(capturedData.fingerData, selectedReader?.model);
      }
      handleCancel();
   }

   return (
      <Modal
         isOpen={showReaderModal}
         onRequestClose={() => handleCancel()}
         style={{
            ...customStyles,
            content: {
               ...customStyles.content,
               width: '870px',
               maxWidth: '90dvw',
               padding: '36px'
            }
         }}
         contentLabel={`Capturar ${captureOptions.type === 'finger' ? 'digital' : ''} com Leitora ${captureOptions.type === 'face' ? 'Facial' : ''}`}
      >
         <div className='modal-header mb-4!'>
            <h2>
               Capturar {captureOptions.type === 'finger' ? 'digital' : ''} com Leitora {captureOptions.type === 'face' ? 'Facial' : ''}
            </h2>
            <button className="close-button" onClick={() => handleCancel()}><IoClose /></button>
         </div>
         <div className='modal-body'>
            <div className="flex flex-row-reverse gap-3">
               <div className="w-[66%]">
                  <div className="w-full mb-2!">
                     <LicenseUnitSelector
                        label={t(`common.license`)}
                        unitId={unit?.id}
                        setUnitId={id => {
                           const selUnit = condominiums.find(unit => unit.id === id);
                           setUnit({
                              ...selUnit
                           });
                        }}
                        type={props.formMode}
                        disabled={isCapturing}
                     />
                  </div>
                  <div className='grid grid-cols-2 w-full gap-2 mb-4!'>
                     <Input
                        placeholder={t("captureModal.searchReaders")}
                        className="input-form search-input w-full h-[44px]!"
                        type="text"
                        value={searchReader}
                        onChange={(e) => setSearchReader(e.target.value)}
                        style={{ backgroundColor: `var(--inputBackgroundColor)` }}
                        disabled={isCapturing}
                     />
                     <Select
                        value={readerModel}
                        onChange={({ target }) => setReaderModel(target.value)}
                        required={true}
                        options={[
                           { value: `0`, text: t('common.model') },
                           ...Object.entries(ReaderModel).map(([, model]) => (
                              { value: model.key, text: model.name }
                           )),
                        ]}
                        disabled={isCapturing}
                     />
                  </div>
                  <div className="w-full flex flex-col gap-2">
                     {isLoading ? (
                        <p className="empty-text">{t('captureModal.loadingReaders')}...</p>
                     ) : (
                        <>
                           {!readers?.length ? (
                              <p className="empty-text">{t('captureModal.emptyState')}</p>
                           ) : (
                              <>
                                 {readers?.map((reader) => (
                                    <div
                                       key={reader.id}
                                       className="flex items-center gap-2"
                                    >
                                       <div className={`flex items-center gap-2 px-4! py-3! rounded-lg transition-colors border! border-(--borderColor)! flex-1`}>
                                          <span className="text-(--textColor)">{reader.name}</span>
                                       </div>
                                       <Button
                                          onClick={() => {
                                             if (isCapturing && selectedReader?.id === reader.id) {
                                                setIsCapturing(false);
                                                setSelectedReader(null);
                                                setCapturedData(null);
                                                return;
                                             }
                                             handleCapture(reader);
                                          }}
                                          label={isCapturing && selectedReader?.id === reader.id ? t("common.cancel") : t("captureModal.capture")}
                                          className={`h-[39px]! ${isCapturing && selectedReader?.id === reader.id ? "button-white" : ""}`}
                                          disabled={isCapturing && selectedReader?.id !== reader.id}
                                       />
                                    </div>
                                 ))}
                              </>
                           )}
                        </>
                     )}
                  </div>
               </div>
               <div className="w-[34%]">
                  {showCaptureContent()}
               </div>
            </div>
            <div className="w-full flex items-center justify-end flex-row gap-2 mt-3!">
               <Button
                  className={"button-white"}
                  onClick={handleCancel}
                  label={t("common.cancel")}
               />
               <Button
                  onClick={() => handleConfirmCapture()}
                  label={t("common.confirm")}
                  disabled={!capturedData?.faceData && !capturedData?.fingerData}
               />
            </div>
         </div>
      </Modal >
   );
}